import { useState } from 'react';
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import { Menu as MenuIcon } from 'lucide-react';
import { LogoMark } from '@/components/Logo';
import { useMe } from '@/lib/auth';
import type { WorkspaceSummary } from '@/lib/types';
import { Sidebar } from './Sidebar';
import { useCurrentWorkspace } from './useCurrentWorkspace';

import { tr } from '@/i18n';
export interface DashboardContext {
  workspace: WorkspaceSummary;
  workspaces: WorkspaceSummary[];
}

export function useDashboard(): DashboardContext {
  return useOutletContext<DashboardContext>();
}

/** Каркас дашборда: боковая навигация + контент. На мобильных — выезжающее меню. */
export function DashboardLayout() {
  const me = useMe();
  const location = useLocation();
  const { workspaces, current, select } = useCurrentWorkspace();
  const [drawer, setDrawer] = useState(false);

  if (me.isLoading) return <div className="h-full bg-bg" />;
  if (!me.data?.user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!current) return <div className="h-full bg-bg" />;

  return (
    <div className="flex h-full bg-bg">
      <div className="hidden md:block">
        <Sidebar workspaces={workspaces} current={current} onSelect={select} />
      </div>
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setDrawer(false)} />
            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 400, damping: 38 }}
              className="fixed inset-y-0 left-0 z-50 shadow-modal md:hidden"
            >
              <Sidebar workspaces={workspaces} current={current} onSelect={select} onNavigate={() => setDrawer(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <main id="main" tabIndex={-1} className="min-w-0 flex-1 overflow-y-auto">
        <div className="flex h-14 items-center gap-2 border-b border-border px-4 md:hidden">
          <button type="button" aria-label={tr("Меню")} onClick={() => setDrawer(true)} className="inline-flex size-9 items-center justify-center rounded-control hover:bg-hover">
            <MenuIcon className="size-5" />
          </button>
          <LogoMark className="size-6" />
        </div>
        <Outlet context={{ workspace: current, workspaces } satisfies DashboardContext} />
      </main>
    </div>
  );
}
